import classNames from 'classnames'
import { FormattedCurrency } from '@dustin-web/microsite-components/src/i18n'
import type { VariantValue } from '../product-variant-group/product-variant-group.types'
import { getPriceDifference, PriceDifference } from './product-variant-selector.utils'

import './product-variant-selector.scss'

type Props = {
  price: NonNullable<VariantValue['product']>['price']
  variantName: string
  href: string
  unavailableText: string
  showPrice: boolean
  showPricesInclVat: boolean
  selectedPrice: number | null
  showRoundedPrice: boolean
  isSelected: boolean
  isUnavailable: boolean
  affectsMultipleSpecs?: boolean
  affectsMultipleSpecsText: string
  isLoading?: boolean
  showPriceDifference?: boolean
  onSelect: () => void
}

const ProductVariantSelector = ({
  price,
  variantName,
  href,
  selectedPrice,
  isSelected,
  isUnavailable,
  affectsMultipleSpecs = false,
  affectsMultipleSpecsText,
  unavailableText,
  showPrice,
  showPricesInclVat,
  showRoundedPrice,
  showPriceDifference = false,
  isLoading = false,
  onSelect,
}: Props) => {
  let priceDifference: PriceDifference | null = null
  let priceLabel: string | undefined
  let productPrice: number | undefined
  let campaignProductPrice: number | undefined

  if (showPriceDifference) {
    priceDifference = getPriceDifference(price, showPrice, showPricesInclVat, selectedPrice)
    priceLabel = priceDifference
      ? `, ${priceDifference.sign}${showRoundedPrice ? Math.round(priceDifference.amount) : priceDifference.amount}`
      : ''
  } else {
    const originalPrice = showPricesInclVat ? price?.priceIncludingVat : price?.priceExcludingVat
    const campaignPrice = (price?.isCampaign && price.originalPrice) || undefined

    productPrice = showRoundedPrice && originalPrice ? Math.round(originalPrice) : originalPrice
    campaignProductPrice =
      showRoundedPrice && campaignPrice ? Math.round(campaignPrice) : campaignPrice

    priceLabel = productPrice?.toString()
  }

  return (
    <a
      href={href}
      role="radio"
      aria-checked={isSelected}
      aria-disabled={isUnavailable || undefined}
      aria-label={`${variantName}${' '}${priceLabel}`}
      onClickCapture={e => {
        e.preventDefault()
        e.stopPropagation()
        if (!isUnavailable) onSelect()
      }}
      className={classNames('product-variant-selector', {
        selected: isSelected,
        disabled: isUnavailable,
      })}>
      <div className="variant-interaction">
        {isLoading ? (
          <span className="radio-spinner" aria-hidden="true" />
        ) : (
          <span className="radio-indicator" aria-hidden="true" />
        )}
        <span className="variant-label">{variantName}</span>
      </div>

      {!isUnavailable && affectsMultipleSpecs ? (
        <span className="u-fs-12">{affectsMultipleSpecsText}</span>
      ) : (
        isUnavailable && <span className="u-fs-12">{unavailableText}</span>
      )}

      {!isUnavailable && !affectsMultipleSpecs && priceDifference && (
        <div className="price" aria-hidden="true">
          <span>
            {priceDifference.sign}{' '}
            <FormattedCurrency
              amount={
                showRoundedPrice ? Math.round(priceDifference.amount) : priceDifference.amount
              }
            />
          </span>
        </div>
      )}

      {!isUnavailable && !affectsMultipleSpecs && !isSelected && productPrice && (
        <div className="price" aria-hidden="true">
          <span className={classNames({ 'old-price': price?.isCampaign })}>
            <FormattedCurrency amount={campaignProductPrice || productPrice} />
          </span>
          {price?.isCampaign && (
            <span className="campaign-price">
              <FormattedCurrency amount={productPrice} />
            </span>
          )}
        </div>
      )}
    </a>
  )
}

export default ProductVariantSelector
